import { Title, Text, Box } from '@mantine/core';
import { useCallback, useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../../hooks/store';
import { ApiError, ErrorCode } from '../../../models/ApiError';
import RecaptchaAction from '../../../models/RecaptchaAction';
import {
  useStrongAuthRegisterConfirmPhoneMutation,
  useStrongAuthRegisterSendConfirmationCodeMutation,
} from '../../../store/api/strongAuthRegisterApi';
import ConfirmCodeForm from '../../ForgotPassword/ConfirmCode/ConfirmCodeForm';
import { phoneConfirmed } from '../../../store/strongAuthRegisterSlice';

const PhoneConfirmation = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const tempToken = useAppSelector((state) => state.strongAuthRegister.tempToken);

  const [confirmPhone, confirmPhoneQueryState] = useStrongAuthRegisterConfirmPhoneMutation();
  const [sendConfirmationCode, sendConfirmationCodeQueryState] = useStrongAuthRegisterSendConfirmationCodeMutation();

  const getSendCodeErrorMessage = useCallback(
    (error: ApiError) => {
      const errorCode = error.data?.message;
      if (error.status === 403) {
        return t('api.sessionExpiredError');
      }
      if (errorCode === ErrorCode.SendingCodeFailed) {
        return t('createAccount.sendCode.sendingCodeFailed');
      }
      if (errorCode === ErrorCode.RecaptchaError) {
        return t('api.recaptchaError');
      }
      return t('api.unknownError');
    },
    [t],
  );

  const getVerifyCodeErrorMessage = useCallback(
    (error: ApiError) => {
      const errorCode = error.data?.message;
      if (error.status === 403) {
        return t('api.sessionExpiredError');
      }
      if (errorCode === ErrorCode.ConfirmationCodeVerificationFailed) {
        return t('createAccount.verifyCode.codeVerificationFailedError');
      }
      if (errorCode === ErrorCode.RecaptchaError) {
        return t('api.recaptchaError');
      }
      return t('api.unknownError');
    },
    [t],
  );

  useEffect(() => {
    if (confirmPhoneQueryState.isSuccess) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dispatch(phoneConfirmed(confirmPhoneQueryState.data.tempToken!));
    }
  }, [confirmPhoneQueryState.isSuccess, confirmPhoneQueryState.data?.tempToken, dispatch]);

  useEffect(() => {
    if (sendConfirmationCodeQueryState.isSuccess) {
      toast(t('createAccount.verifyCode.newCodeSent'), { type: 'success' });
    }
    if (sendConfirmationCodeQueryState.isError && sendConfirmationCodeQueryState.error) {
      const error = sendConfirmationCodeQueryState.error as ApiError;
      const errorMessage = getSendCodeErrorMessage(error);
      toast(errorMessage, { type: 'error' });
    }
  }, [
    sendConfirmationCodeQueryState.isSuccess,
    sendConfirmationCodeQueryState.isError,
    sendConfirmationCodeQueryState.error,
    t,
    getSendCodeErrorMessage,
  ]);

  useEffect(() => {
    if (confirmPhoneQueryState.isError && confirmPhoneQueryState.error) {
      const error = confirmPhoneQueryState.error as ApiError;
      const errorMessage = getVerifyCodeErrorMessage(error);
      toast(errorMessage, { type: 'error' });
    }
  }, [confirmPhoneQueryState.isError, confirmPhoneQueryState.error, getVerifyCodeErrorMessage]);

  const handleVerifyCode = useCallback(
    async (code: string) => {
      if (executeRecaptcha) {
        const recaptchaToken = await executeRecaptcha(RecaptchaAction.StrongAuthRegisterConfirmPhone);
        confirmPhone({ code: code, tempToken: tempToken || '', recaptchaToken: recaptchaToken });
      }
    },
    [executeRecaptcha, confirmPhone, tempToken],
  );

  const handleResendCode = useCallback(async () => {
    if (executeRecaptcha) {
      const recaptchaToken = await executeRecaptcha(RecaptchaAction.StrongAuthRegisterSendConfirmationCode);
      sendConfirmationCode({ contactType: 'phone', tempToken: tempToken || '', recaptchaToken });
      confirmPhoneQueryState.reset();
    }
  }, [executeRecaptcha, sendConfirmationCode, tempToken, confirmPhoneQueryState]);

  return (
    <Box
      sx={(theme) => ({
        width: '60% !important',
        [theme.fn.smallerThan(960)]: {
          width: '100% !important',
        },
      })}
    >
      <Title
        sx={(theme) => ({
          color: theme.colors.brandGray,
          fontFamily: 'ProximaNovaBold',
          fontSize: '1.25rem',
          fontWeight: 700,
          letterSpacing: '0.01em',
          [theme.fn.smallerThan(800)]: {
            fontSize: '2.2rem',
          },
          [theme.fn.smallerThan(600)]: {
            fontSize: '1.6rem',
          },
        })}
      >
        {t('strongAuthRegister.phoneConfirmationStep.title')}
      </Title>
      <Text
        sx={(theme) => ({
          '&': {
            marginTop: theme.other.spacing(2),
            color: '#828282',
          },
        })}
      >
        <Text>{t('strongAuthRegister.phoneConfirmationStep.description')}</Text>
      </Text>
      <ConfirmCodeForm
        onSubmitCode={handleVerifyCode}
        onResendCode={handleResendCode}
        loading={false}
        resendCodeLoading={false}
        error={''}
      />
    </Box>
  );
};

export default PhoneConfirmation;
